import { Button, Checkbox, Grid, Radio, RadioGroup, Snackbar, Stack, Tooltip } from "@mui/material";
import MuiAlert from '@mui/material/Alert';
import { useSelector, useDispatch } from "react-redux";
import { RootState, store, AppDispatch } from "../state/store";
import { useEffect, useState, useCallback, useMemo, Fragment, useRef } from "react";
import { IDeliveryType, IOtherControl, IPackage, IExpression } from "../models/configuration/plugin/IConfiguration";
import { useTypedSelector } from "../hooks/useTypedSelector";
import { useHostDetails } from "../hooks/useHostDetails";
import AttachmentsCard from "../components/AttachmentsCard";
import OrderTabs from "../components/order/OrderTabs";
import SelectToPrintCard from "../components/order/SelectToPrintCard";
import { GetControlOptions } from "../utils/controlOptionsHelper";
import { IControlsOptionsHelper } from "../models/IControlsOptionsHelper";
import { GetExpressionEvaluationResults, addRegexExp, isPackageBasedExpression, enhanceFieldIdValuePairs } from "../utils/expressionEvaluationHelper";
import { PANEL_IDS, REQUEST_TYPES, REQUEST_TYPE_LABEL, CX_Docutech_Selected_Package, CX_Docutech_Delivery_Types } from "../utils/constants";
import GenericDialog from "../components/shared/GenericDialog";
import EvaluationResults from "../components/order/EvaluationResults";
import { AlertSeverity } from "../constants/AlertTypes";
import { IDocListItem } from "../models/IDocListResponse";
import { IRequestId } from "../models/IRequestIds";
import Footer from "../layout/Footer";
import { SpecialDeliveryTypes, FullfillmentShippingOptions, SpecialClosingTypes, DeliveryTypes } from "../enums/SpecialDeliveryTypes";
import { fetchEvaluatedPackages, fetchEvaluatedDeliveryMethods, fetchEvaluatedOtherControls } from "../state/slices/importExpressionValidationSlice";
import ConfigLoad from "../components/shared/config/ConfigLoad";
import { IEvaluatedPackage, IEvaluatedOtherControl, IExpressionEvaluation } from "../models/IExpressionValidation";
import ExpressionService from "../services/expressionService";
import { setSelectedPackage, setOrderPackageId, setOrderAltPackageId, setOrderParentPackageId, setOrderSelectedDeliveryMethods, setOrderSelectedDocumentList, setOrderRequestTypeVisible, setOrderRequestTypeEnable, setOrderRequestTypeTitle, setOrderRequestType } from "../state/appSlice";
import { useEnhancedFieldIdValuePairs } from "../hooks/useEnhancedFieldIdValuePairs";
import {
    StyledMainContainer,
    StyledContentContainer,
    StyledTabsContainer,
    StyledButtonContainer,
    StyledPackageCard,
    StyledDeliveryCard,
    StyledPackageCardContent,
    StyledPackageCardHeader,
    StyledDeliveryCardHeader,
    StyledDeliveryCardContent,
    StyledPackageFormControl,
    StyledPackageTooltip,
    StyledPackageFormControlLabel,
    StyledDeliveryFormGroup,
    StyledDeliveryFormControlLabel
} from './Order.styles';
import { DtDialog } from "../components/ui/dialog";

// Generalizes the historical "push the one fixed secondary Id" behavior: for each of the
// eSign/eDisclose/eClose parents present in deliveryTypes, expand to every configured delivery
// type Id under that parent (not just the single fixed constant), so dynamically-added secondary
// methods (e.g. "eSign-a1b2c3d4-...") become selectable alongside the fixed ones. Mutates and returns
// deliveryTypes in place to match the existing push-style call sites in this file.
export const expandSecondaryDeliveryTypes = (
    deliveryTypes: string[],
    configuredDeliveryTypes: IDeliveryType[] | null | undefined
): string[] => {
    if (!configuredDeliveryTypes?.length) return deliveryTypes;

    [DeliveryTypes.ESIGN, DeliveryTypes.EDISCLOSE, DeliveryTypes.ECLOSE].forEach(parent => {
        const parentLower = parent.toLowerCase();
        if (deliveryTypes.some(dt => dt?.toLowerCase().startsWith(parentLower))) {
            configuredDeliveryTypes.forEach(cdt => {
                const idLower = cdt.Id.toLowerCase();
                const alreadyIncluded = deliveryTypes.some(dt => dt?.toLowerCase() === idLower);
                if (idLower.startsWith(parentLower) && idLower !== parentLower && !alreadyIncluded) {
                    deliveryTypes.push(cdt.Id);
                }
            });
        }
    });

    return deliveryTypes;
};

const OrderPage = () => {

    //Page Access/Visibility states
    const appState = useSelector((state: RootState) => state.appSlice)
    const { host } = useHostDetails();
    const otherControlsState = useSelector((state: RootState) => state.configPlugInData.data?.Configuration.OtherControls ?? null)
    const packageTypeState = useTypedSelector((state) => state.configPlugInData.data?.Configuration.Packages ?? null);
    const deliveryMethodState = useSelector((state: RootState) => state.configPlugInData.data?.Configuration.DeliveryTypes ?? null);
    const configMisc = useTypedSelector((state) => state.configPlugInData.data?.Configuration?.DefaultValues);
    const allowMultiDelivery = useTypedSelector((state) => state.configPlugInData.data?.Configuration?.AllowMultiDelivery);
    const dispatch: AppDispatch = useDispatch();
    const { evaluatedPackages, evaluatedDeliveryMethods, evaluatedOtherControls, loading } = useSelector((state: RootState) => state.expressionValidationData);
    const [importLoading, setImportLoading] = useState<boolean>(false);
    const [autoSelectLoading, setAutoSelectLoading] = useState<boolean>(false);

    const [packageTypes, setPackageTypes] = useState<Array<IPackage>>([]);
    const [deliveryMethods, setDeliveryMethods] = useState<Array<IDeliveryType>>([])
    const [otherControls, setOtherControls] = useState<Array<IOtherControl>>([])
    const [selectedDeliveryMethods, setSelectedDeliveryMethods] = useState<Array<IDeliveryType>>([]);

    const [allPackageTypes, setAllPackageTypes] = useState<Array<IPackage>>([]);
    const [attachmentsVisible, setAttachmentsVisible] = useState(false);
    const [attachmentsEnable, setAttachmentsEnable] = useState(false);
    const [selectedVisible, setSelectedVisible] = useState(false);
    const [selectedEnable, setSelectedEnable] = useState(false);
    const [requestTypeVisible, setRequestTypeVisible] = useState(false);
    const [requestTypeEnable, setRequestTypeEnable] = useState(false);
    const [requestTypeTitle, setRequestTypeTitle] = useState(REQUEST_TYPE_LABEL);
    const [attachmentsTitle, setAttachmentsTitle] = useState("Attachments");
    const [selectToPrintTitle, setSelectToPrintTitle] = useState("Selected");
    const requestType = appState.orderState.requestType || configMisc?.DefaultRequestType || REQUEST_TYPES.LIGHTS_OUT;
    const [packageId, setPackageId] = useState('');
    const [autoEvaluationResultsOpen, setAutoEvaluationResultsOpen] = useState(false);
    const [altPackageId, setAltPackageId] = useState('');
    const [logsTabVisible, setLogsTabVisible] = useState(false);
    const [logsTabEnable, setLogsTabEnable] = useState(false);
    const [requestResponseTabVisible, setRequestResponseTabVisible] = useState(false);
    const [requestResponseTabEnable, setRequestResponseTabEnable] = useState(false);
    const [logsTabTitle, setLogsTabTitle] = useState("LOGS");
    const [requestResponseTabTitle, setRequestResponseTabTitle] = useState("REQUEST/RESPONSE");
    const [parentPackageId, setParentPackageId] = useState('');
    const requests = useTypedSelector<Array<IRequestId>>((state) => appState.requestIds);
    const expressionService = useMemo(() => new ExpressionService(), []);
    const accessToken = useSelector((state: RootState) => state.appSlice.accessToken);
    const orderSelectedDeliveryMethods = useSelector((state: RootState) => state.appSlice.orderState.selectedDeliveryMethods);
    const enhancedFieldIdValuePairs = useEnhancedFieldIdValuePairs();
    const [isPackageFormControlHovered, setIsPackageFormControlHovered] = useState(false);
    const [lastProcessedEvaluations, setLastProcessedEvaluations] = useState<string>('');
    const [isEvaluatingExpressions, setIsEvaluatingExpressions] = useState<boolean>(false);

    // Check if item has expressions that depend on the selected package
    const hasPackageBasedExpressions = useCallback((item: IDeliveryType | IPackage): boolean => {
        return item.Expressions?.some(expr => 
            expr.Enabled && isPackageBasedExpression(expr.ExpressionString)
        ) ?? false;
    }, []);

    // TPO-aware package filtering with persona/user security checks
    const getFilteredPackages = useCallback((packages: IPackage[]): IPackage[] => {
        return packages.filter(pkg => {
            // Always check if package is enabled
            if (!pkg.Enabled) {
                return false;
            }

            // If in TPO Connect environment and TPO is enabled, check EnableForTpoUser
            if (host.isTpoConnectUsable) {
                return pkg.EnableForTpoUser ?? false;
            }

            // If in TPO Connect but TPO not enabled in config, don't show packages
            if (host.isTpoConnect) {
                return false;
            }

            // Check persona and user access using existing GetControlOptions helper
            const controlOptions = GetControlOptions(pkg, pkg.Caption || '', appState);
            return controlOptions.IsEnabled;
        });
    }, [host.isTpoConnect, host.isTpoConnectUsable, appState.userPersonas, appState.userId]);

    // TPO-aware delivery method filtering
    const getFilteredDeliveryMethods = useCallback((deliveryMethods: IDeliveryType[]): IDeliveryType[] => {
        return deliveryMethods.filter(dm => {
            // Always check if delivery method is enabled
            if (!dm.Enabled) {
                return false;
            }

            // If in TPO Connect environment and TPO is enabled, check EnableForTpoUser
            if (host.isTpoConnectUsable) {
                return dm.EnableForTpoUser ?? false;
            }

            // If in TPO Connect but TPO not enabled in config, don't show delivery methods
            if (host.isTpoConnect) {
                return false;
            }

            // Not in TPO Connect environment, use current logic
            return true;
        });
    }, [host.isTpoConnect, host.isTpoConnectUsable]);

    // Filter delivery methods based on their business rule evaluation results
    const filterDeliveryMethodsByEvaluation = useCallback((
        deliveryMethods: IDeliveryType[],
        evaluatedDeliveryMethods: any[]
    ): IDeliveryType[] => {
        // No evaluation results yet? Just show enabled methods
        if (!Array.isArray(evaluatedDeliveryMethods) || !evaluatedDeliveryMethods?.length) {
            return deliveryMethods.filter(item => item.Enabled);
        }

        return deliveryMethods.filter((deliveryMethod) => {
            const matchingEvaluation = evaluatedDeliveryMethods.find((evalItem) => 
                evalItem?.validConfigType?.Id?.toLowerCase() === deliveryMethod.Id.toLowerCase()
            ); 
            if (!matchingEvaluation) {
                return deliveryMethod.Enabled;
            }

            // Get only enabled expressions
            const enabledExpressions = deliveryMethod.Expressions?.filter(expr => expr.Enabled) || [];

            // If no enabled expressions, just check if delivery method is enabled
            if (enabledExpressions.length === 0) {
                return deliveryMethod.Enabled;
            }

            // Check if we have evaluation results for all enabled expressions
            if (matchingEvaluation.evaluationResult.result.length < enabledExpressions.length) {
                return false; // Fail safe - if we don't have enough results, hide the method
            }

            // All enabled expressions must pass
            const allExpressionsTrue = enabledExpressions.every((expr: any, enabledIndex: number) => {
                const evaluationResult = matchingEvaluation.evaluationResult.result[enabledIndex];
                return evaluationResult?.evaluationResult === true;
            });
            return allExpressionsTrue && matchingEvaluation.validConfigType.Enabled;
        });
    }, []);


    //select to print 
    const [documentList, setDocumentList] = useState<Array<IDocListItem>>([]);
    const [selectedDocumentList, setSelectedDocumentList] = useState<Array<IDocListItem>>([])

    // Alert
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage] = useState('MESSAGE');
    const [alertSeverity] = useState<AlertSeverity>('info');
    const handleSnackBarClose = (_event: any) => { setAlertOpen(false); }

    // Runs when user selects a package - updates delivery methods intelligently
    const handlePackageChange = async (pkgType: string, delTypes: string[], packageCaption?: string) => {
        // Remember the selected package for other components
        const selectedPackageValue = packageCaption || pkgType;
        dispatch(setSelectedPackage(selectedPackageValue));

        // Get delivery methods that are valid for this package
        const lowerCaseDeliveryMethods = delTypes.map(d => d?.toLocaleLowerCase());
        const validDeliveryMethods = deliveryMethodState?.filter((dm: IDeliveryType) => 
            lowerCaseDeliveryMethods.includes(dm.Id.toLocaleLowerCase())
        ) || [];
        
        // Apply TPO filtering
        const allValidDeliveryMethods = getFilteredDeliveryMethods(validDeliveryMethods);

        // Smart split: methods that care about package vs methods that don't
        const methodsWithPackageExpressions = allValidDeliveryMethods.filter(hasPackageBasedExpressions);
        const methodsWithoutPackageExpressions = allValidDeliveryMethods.filter(dm => !hasPackageBasedExpressions(dm));

        // Methods without package rules: use existing results
        let finalMethodsWithoutPackageExpressions = methodsWithoutPackageExpressions.filter(dm => dm.Enabled);
        if (evaluatedDeliveryMethods?.length) {
            finalMethodsWithoutPackageExpressions = filterDeliveryMethodsByEvaluation(
                methodsWithoutPackageExpressions,
                evaluatedDeliveryMethods
            );
        }

        let finalMethodsWithPackageExpressions: IDeliveryType[] = [];

        // Methods with package rules: get fresh evaluation from server
        if (methodsWithPackageExpressions.length > 0) {
            try {
                setIsEvaluatingExpressions(true);
                const result = await dispatch(fetchEvaluatedDeliveryMethods(methodsWithPackageExpressions)).unwrap();
                
                if (result && Array.isArray(result)) {
                    finalMethodsWithPackageExpressions = filterDeliveryMethodsByEvaluation(
                        methodsWithPackageExpressions,
                        result
                    );
                }
            } catch (error) {
                console.error('Error evaluating delivery methods with package expressions:', error);
            } finally {
                setIsEvaluatingExpressions(false);
            }
        }
        
        // Combine and display final results
        const allFilteredMethods = [...finalMethodsWithoutPackageExpressions, ...finalMethodsWithPackageExpressions];
        
        setDeliveryMethods(allFilteredMethods.sort((a, b) => 
            (Number(a.Order ?? 0) - Number(b.Order ?? 0))
        ));
        
        setSelectedDocumentList([]);
        setDocumentList([]);
    }

    const processAutoSelect = async (delTypesWithAutoSelect: IDeliveryType[]) => {
        if (requestType !== REQUEST_TYPES.LIGHTS_OUT) return; // Only perform autoselect during LIGHTS_OUT mode

        const autoSelectExpressions: string[] = delTypesWithAutoSelect?.map((dt: IDeliveryType) => dt.AutoSelectWhen).filter((exp): exp is string => exp !== undefined);

        if (autoSelectExpressions && autoSelectExpressions?.length > 0) {
            setAutoSelectLoading(true);

            const newExpArray: IExpression[] = autoSelectExpressions.map((expression) => ({
                ExpressionString: expression,
                ExpressionName: "",
                ExpressionMessage: "",
                Enabled: true
            }));

            const results: IExpression[] = await GetExpressionEvaluationResults(newExpArray, enhancedFieldIdValuePairs, null);
            const expressionArray = results.map((exp: IExpression) => exp.ExpressionString);
            const evalResults: IExpressionEvaluation[] = (await expressionService.validateExpressions(accessToken, expressionArray)).result;

            const validationResultsMap = new Map(evalResults.filter((res) => res.evaluationResult)
                .map((res) => [res.expression, res.evaluationResult]));

            setSelectedDeliveryMethods((selectedDeliveryMethods) => {
                const updatedMethods = [...selectedDeliveryMethods];

                delTypesWithAutoSelect?.forEach((dt: IDeliveryType) => {
                    const regexExp = addRegexExp(dt.AutoSelectWhen ?? "", enhancedFieldIdValuePairs, null);
                    if (dt.AutoSelectWhen && regexExp && validationResultsMap.has(regexExp) && validationResultsMap.get(regexExp)) {
                        if (!updatedMethods.some((d) => d.Id === dt.Id)) {
                            updatedMethods.push(dt);
                        }
                    }
                });
                return updatedMethods;
            });
            setAutoSelectLoading(false);
        }
    };

    const processPackageAutoSelect = async (packagesWithAutoSelect: IPackage[]) => {
        
        setAutoSelectLoading(true);

        try {
            const expressionBatch = packagesWithAutoSelect.map(pkg => {
                const packageName = pkg.Caption || pkg.Id;
                const fieldContext = enhanceFieldIdValuePairs(enhancedFieldIdValuePairs, packageName);
                return addRegexExp(pkg.AutoSelectWhen!, fieldContext, packageName);
            });

            const evalResult = await expressionService.validateExpressions(accessToken, expressionBatch);
            
            const selectedIndex = evalResult?.result?.findIndex(result => result.evaluationResult === true);
            if (selectedIndex >= 0) {
                const selectedPackage = packagesWithAutoSelect[selectedIndex];
                const packageName = selectedPackage.Caption || selectedPackage.Id;
                
                setPackageId(selectedPackage.Id ?? "");
                dispatch(setSelectedPackage(packageName));
                setAltPackageId(selectedPackage.AlternatePackageId ?? "");
                setParentPackageId(selectedPackage.ParentPackageId ?? "");
                
                await handlePackageChange(packageName, selectedPackage.ValidDeliveryTypes ?? [], packageName);
            }
        } catch (error) {
            console.error('Error evaluating package auto-select:', error);
        } finally {
            setAutoSelectLoading(false);
        }
    };

    const setControlOptions = (opts: IControlsOptionsHelper, otherControl: any, setVisible: any, setEnable: any, setTitle: any) => {
        if (otherControl && opts) {
            setVisible(opts.IsVisible && otherControl);
            setEnable(opts.IsEnabled && otherControl);
        }
        else {
            setVisible(false);
            setEnable(false);
        }
        setTitle(opts.Title);
    }

    const handleDeliveryMethodClick = (e: any) => {
        const checked = e.target.checked;
        const val = e.target.value;

        const deliveryObj = deliveryMethods.find(d => d.Id === val);
        if (!deliveryObj) return;

        if (allowMultiDelivery ?? true) {
            if (checked) {
                setSelectedDeliveryMethods(prev =>
                    prev.some(d => d.Id === val) ? prev : [...prev, deliveryObj]
                );
            } else {
                setSelectedDeliveryMethods(prev => prev.filter(dm => dm.Id !== val));
            }
        } else {
            if (checked) {
                setSelectedDeliveryMethods([deliveryObj]);
            }
        }
    };

    const addSpecialDeliveryTypes = (deliveryTypes: string[]) => {
        let updatedDeliveryTypes = [...deliveryTypes] //avoid mutating the array

        //Adding custom delivery
        updatedDeliveryTypes.push(SpecialDeliveryTypes.PrintInPerson);

        //Adding special delivery types for FullFillmentShippingOptions
        if (updatedDeliveryTypes.includes(DeliveryTypes.MAIL)) {
            for (let deliveryType in FullfillmentShippingOptions) {
                updatedDeliveryTypes.push(`${DeliveryTypes.MAIL}-${deliveryType}`)
            }
        }

        //Adding special delivery types for SpecialClosingTypes
        if (updatedDeliveryTypes.includes(DeliveryTypes.ECLOSE)) {
            for (let deliveryType in SpecialClosingTypes) {
                updatedDeliveryTypes.push(`${DeliveryTypes.ECLOSE}-${deliveryType}`)
            }
        }

        return expandSecondaryDeliveryTypes(updatedDeliveryTypes, deliveryMethodState);
    }

    // Show packages that belong to current page and pass their business rules
    const filterPackagesByGroup = useCallback((evalPackages: IEvaluatedPackage[]) => {
        // Get packages for this page/group, applying TPO filtering consistently
        const pagePackages = allPackageTypes?.filter((pkg) =>
            pkg.Group === appState.page
        ) || [];
        const groupPackages = getFilteredPackages(pagePackages);

        if (!evalPackages?.length) {
            setPackageTypes(groupPackages.sort((a, b) => (a.Order ?? 0) - (b.Order ?? 0)));
            return;
        }

        // Filter based on business rule results
        const availablePackages = groupPackages.filter((pkg) => {
            if (!pkg.Expressions?.some(expr => expr.Enabled)) {
                return true; // No rules = always show
            }

            const matchingEvaluation = evalPackages.find((evalPkg) => 
                evalPkg?.validConfigType?.Id?.toLowerCase() === pkg.Id?.toLowerCase()
            );

            if (!matchingEvaluation) return true;

            // All rules must pass
            return matchingEvaluation.evaluationResult.result.every((res: any, index: number) => {
                const originalExpression = pkg.Expressions?.[index];
                if (!originalExpression?.Enabled) return true;
                return res.evaluationResult;
            });
        });

        setPackageTypes(availablePackages.sort((a, b) => (a.Order ?? 0) - (b.Order ?? 0)));
    }, [allPackageTypes, appState.page, getFilteredPackages]);

    const reEvaluateOtherControls = useCallback(async (context: string, contextData?: Record<string, any>) => {
        if (!otherControlsState?.length) return;

        try {
            setIsEvaluatingExpressions(true);
            await dispatch(fetchEvaluatedOtherControls(otherControlsState)).unwrap();
        } catch (error) {
            console.error(`Error re-evaluating other controls after ${context}.`, {
                error,
                otherControlsCount: otherControlsState?.length,
                ...contextData
            });
        } finally {
            setIsEvaluatingExpressions(false);
        }
    }, [otherControlsState, dispatch]);

    const checkPlaceholdersAndReEvaluate = useCallback((stateValue: any, placeholder: string, context: string) => {
        if (stateValue !== undefined && stateValue !== null && otherControlsState?.length) {
            // Check if any other control has the specified placeholder in expressions
            const hasPlaceholders = otherControlsState.some(control => 
                control.Expressions?.some(expr => 
                    expr.Enabled && expr.ExpressionString.includes(placeholder)
                )
            );
            
            if (hasPlaceholders) {
                reEvaluateOtherControls(context, { stateValue });
            }
        }
    }, [otherControlsState, reEvaluateOtherControls]);

    // Expand delivery types (e.g., Mail → Mail-FedEx, Mail-UPS); re-runs when config switches
    useEffect(() => {
        if (packageTypeState) {
            const updatedPackageList = packageTypeState.map((pkg: IPackage) => ({
                ...pkg,
                ValidDeliveryTypes: addSpecialDeliveryTypes(pkg.ValidDeliveryTypes),
            }));
            setAllPackageTypes(updatedPackageList);
        }
    }, [packageTypeState]);

    // Initialize requestType from configuration when configMisc becomes available
    useEffect(() => {
        if (configMisc?.DefaultRequestType && !appState.orderState.requestType) {
            dispatch(setOrderRequestType(configMisc.DefaultRequestType));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [configMisc?.DefaultRequestType]);

    useEffect(() => {
        // Clear order selection when page changes or plugin config switches
        setPackageId('');
        setAltPackageId('');
        setParentPackageId('');
        setDeliveryMethods([]);
        setSelectedDeliveryMethods([]);
        setSelectedDocumentList([]);
        setLastProcessedEvaluations('');
        dispatch(setSelectedPackage(null));

        // Filter packages when page or config changes
        if (allPackageTypes?.length) {
            filterPackagesByGroup(evaluatedPackages || []);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allPackageTypes, appState.page]);

    // Business rule evaluation can remove the selected package from the list after it was
    // selected, and in-flight evaluations can repopulate delivery methods after that. Keep
    // the panel empty unless a currently offered package is selected, otherwise its delivery
    // methods linger on screen with no package radio to match them.
    useEffect(() => {
        const selectionIsOffered = Boolean(packageId) && getFilteredPackages(packageTypes).some(pkg => pkg.Id === packageId);
        if (selectionIsOffered || (!packageId && deliveryMethods.length === 0)) {
            return;
        }

        setPackageId('');
        setAltPackageId('');
        setParentPackageId('');
        setDeliveryMethods([]);
        setSelectedDeliveryMethods([]);
        setSelectedDocumentList([]);
        setLastProcessedEvaluations('');
        dispatch(setSelectedPackage(null));
    }, [packageTypes, packageId, deliveryMethods, getFilteredPackages, dispatch]);

    // Handle package filtering when evaluatedPackages changes
    useEffect(() => {
        if (allPackageTypes?.length) {
            filterPackagesByGroup(evaluatedPackages || []);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [evaluatedPackages, allPackageTypes]);

    useEffect(() => {
        const fetchData = async () => {
            // Evaluate packages and delivery methods initially
            // We'll re-evaluate items with package expressions when package is selected
            if (packageTypeState?.length) {
                await dispatch(fetchEvaluatedPackages(packageTypeState));
            }

            if (deliveryMethodState?.length) {
                // Apply TPO filtering before evaluating
                const tpoFilteredDeliveryMethods = getFilteredDeliveryMethods(deliveryMethodState);
                await dispatch(fetchEvaluatedDeliveryMethods(tpoFilteredDeliveryMethods));
            }

            if (otherControlsState?.length) {
                await dispatch(fetchEvaluatedOtherControls(otherControlsState));
            }
        };
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [packageTypeState, deliveryMethodState, otherControlsState, dispatch]);

    useEffect(() => {
        setImportLoading(loading);
    }, [loading]);

    useEffect(() => {
        if (!loading && evaluatedPackages?.length && evaluatedDeliveryMethods?.length && evaluatedOtherControls?.length) {
            let ocs: IOtherControl[] = otherControls;

            const filterOtherControls = evaluatedOtherControls.filter(x => {
                const results = x.evaluationResult.result || [];
                // Treat controls with no evaluated expressions as visible to avoid unintentionally hiding them
                if (results.length === 0) {
                    return true;
                }
                return results.some(res => res.evaluationResult);
            });
            ocs = otherControlsState?.filter(item => filterOtherControls.map((filteredItem: IEvaluatedOtherControl) => { return filteredItem.validConfigType }).includes(item)) ?? [];

            if (JSON.stringify(ocs) !== JSON.stringify(otherControls)) {
                setOtherControls(ocs);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [evaluatedPackages, evaluatedDeliveryMethods, evaluatedOtherControls, loading]);

    // Handle delivery method filtering when package is selected and evaluations are updated
    useEffect(() => {
        if (evaluatedDeliveryMethods?.length && packageId && deliveryMethodState?.length && allPackageTypes?.length) {
            // Create a unique key to prevent duplicate processing
            const evaluationKey = `${packageId}-${evaluatedDeliveryMethods.length}-${evaluatedDeliveryMethods[0]?.validConfigType?.Id || ''}`;
            
            if (evaluationKey === lastProcessedEvaluations) {
                return;
            }
            
            // Get delivery methods for the selected package
            const selectedPackage = allPackageTypes.find(pkg => pkg.Id === packageId);
            
            if (selectedPackage?.ValidDeliveryTypes?.length) {
                const lowerCaseDeliveryMethods = selectedPackage.ValidDeliveryTypes.map(d => d?.toLocaleLowerCase());
                const validDeliveryMethods = deliveryMethodState.filter((dm: IDeliveryType) => 
                    lowerCaseDeliveryMethods.includes(dm.Id.toLocaleLowerCase())
                );
                
                // Apply TPO filtering
                const filteredDeliveryMethods = getFilteredDeliveryMethods(validDeliveryMethods);

                // Use existing evaluation results for all delivery methods
                const finalFilteredMethods = filterDeliveryMethodsByEvaluation(
                    filteredDeliveryMethods, 
                    evaluatedDeliveryMethods
                );

                setDeliveryMethods(finalFilteredMethods?.sort((a: IDeliveryType, b: IDeliveryType) => (Number(a.Order ?? 0) - Number(b.Order ?? 0))) ?? []);
                setSelectedDeliveryMethods([]);
                setLastProcessedEvaluations(evaluationKey);

                // Handle autoselect for delivery types that have autoselect expressions
                const delTypesWithAutoSelect = finalFilteredMethods.filter((dt: IDeliveryType) => dt.AutoSelectWhen);

                if (delTypesWithAutoSelect.length > 0) {
                    processAutoSelect(delTypesWithAutoSelect);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [evaluatedDeliveryMethods, packageId]);

    useEffect(() => {
        if (packageTypes?.length && !packageId && !isEvaluatingExpressions) {
            const filteredPackages = getFilteredPackages(packageTypes);
            const packagesWithAutoSelect = filteredPackages.filter(pkg => pkg?.AutoSelectWhen?.trim());
            
            if (packagesWithAutoSelect.length > 0) {
                processPackageAutoSelect(packagesWithAutoSelect);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [packageTypes, packageId, isEvaluatingExpressions, getFilteredPackages]);

    // Clear delivery methods and selections when switching to LIGHTS_ON mode
    useEffect(() => {
        if (requestType === REQUEST_TYPES.LIGHTS_ON) {
            setSelectedDeliveryMethods([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestType]);

    useEffect(() => {
        if (otherControls) {
            const otherControlOptions = otherControls;

            const attachmentControlOpts = otherControlOptions?.find(c => c.Id === PANEL_IDS.ATTACHMENTS);
            const selectDocOpts = otherControlOptions?.find(c => c.Id === PANEL_IDS.SELECT_TO_PRINT);
            const requestTypeControlOpts = otherControlOptions?.find(c => c.Id === PANEL_IDS.REQUEST_TYPE);
            const logsTabControlOpts = otherControlOptions?.find(c => c.Id === PANEL_IDS.LOGS_TAB);
            const requestResponseTabControlOpts = otherControlOptions?.find(c => c.Id === PANEL_IDS.REQUEST_RESPONSE_TAB);
            const currentAppState = store.getState().appSlice;

            const attachOptions = GetControlOptions(attachmentControlOpts, "Add Attachments", currentAppState);
            const selectDocOptions = GetControlOptions(selectDocOpts, "Select to Print", currentAppState);
            const requestTypeOptions = GetControlOptions(requestTypeControlOpts, REQUEST_TYPE_LABEL, currentAppState);
            const logsTabControlOptions = GetControlOptions(logsTabControlOpts, "Logs", currentAppState);
            const requestResponseTabControlOptions = GetControlOptions(requestResponseTabControlOpts, "Request/Response", currentAppState);

            setControlOptions(attachOptions, attachmentControlOpts, setAttachmentsVisible, setAttachmentsEnable, setAttachmentsTitle);
            setControlOptions(selectDocOptions, selectDocOpts, setSelectedVisible, setSelectedEnable, setSelectToPrintTitle);
            setControlOptions(requestTypeOptions, requestTypeControlOpts, setRequestTypeVisible, setRequestTypeEnable, setRequestTypeTitle);
            setControlOptions(logsTabControlOptions, logsTabControlOpts, setLogsTabVisible, setLogsTabEnable, setLogsTabTitle);
            setControlOptions(requestResponseTabControlOptions, requestResponseTabControlOpts, setRequestResponseTabVisible, setRequestResponseTabEnable, setRequestResponseTabTitle);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [otherControls])

    // Sync local state with Redux on changes
    useEffect(() => {
        dispatch(setOrderPackageId(packageId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [packageId]);

    useEffect(() => {
        dispatch(setOrderAltPackageId(altPackageId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [altPackageId]);

    useEffect(() => {
        dispatch(setOrderParentPackageId(parentPackageId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parentPackageId]);

    useEffect(() => {
        dispatch(setOrderSelectedDeliveryMethods(selectedDeliveryMethods));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDeliveryMethods]);

    useEffect(() => {
        dispatch(setOrderSelectedDocumentList(selectedDocumentList));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDocumentList]);

    useEffect(() => {
        dispatch(setOrderRequestTypeVisible(requestTypeVisible));
        dispatch(setOrderRequestTypeEnable(requestTypeEnable));
        dispatch(setOrderRequestTypeTitle(requestTypeTitle));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestTypeVisible, requestTypeEnable, requestTypeTitle]);

    // Re-evaluate other controls when package changes - only if controls have package placeholders
    useEffect(() => {
        checkPlaceholdersAndReEvaluate(appState.selectedPackage, CX_Docutech_Selected_Package, 'package selection change');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appState.selectedPackage, checkPlaceholdersAndReEvaluate]);

    // Re-evaluate other controls when delivery methods change - only if controls have delivery placeholders
    useEffect(() => {
        checkPlaceholdersAndReEvaluate(orderSelectedDeliveryMethods, CX_Docutech_Delivery_Types, 'delivery method selection change');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderSelectedDeliveryMethods, checkPlaceholdersAndReEvaluate]);

    return (
        <div>
                {(importLoading || autoSelectLoading) && !appState.loadingState.loading && (
                    <ConfigLoad loading={true} message="Loading" />
                )}

                <StyledMainContainer component="main" alignItems="top">
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={alertOpen}
                autoHideDuration={4000}
                onClose={handleSnackBarClose}
            >
                <MuiAlert
                    onClose={handleSnackBarClose}
                    severity={alertSeverity}
                    sx={{ width: '100%', fontSize: '1.2rem', padding: '12px 16px' }}>
                    {alertMessage}
                </MuiAlert>
            </Snackbar>
            <StyledContentContainer maxWidth="xl">
                <Grid container spacing={3} justifyContent="space-between" alignItems="stretch">
                    <Grid item xs={12} md={6} lg={6}>
                        <StyledPackageTooltip
                            show={isPackageFormControlHovered || requestType === REQUEST_TYPES.LIGHTS_ON}
                            title={'Choose Package type. Doing so will present valid delivery types for this package.'}
                            PopperProps={{ sx: { display: isPackageFormControlHovered || requestType === REQUEST_TYPES.LIGHTS_ON ? 'none' : 'block' } }}
                        >
                            <StyledPackageCard className="responsive-card">
                                <StyledPackageCardHeader
                                    title="Package">
                                </StyledPackageCardHeader>
                                <StyledPackageCardContent>
                                    <StyledPackageFormControl>
                                        <RadioGroup
                                            value={packageId}
                                            name={`${appState.page?.replace(/\s/g, '')}-radio}`}>
                                            {getFilteredPackages(packageTypes).map((p, i) =>
                                                <Fragment key={`packageType-${i}`}>
                                                    <StyledPackageFormControlLabel
                                                        packageColor={!p.ParentPackageId ? undefined : configMisc?.AlternatePackageColor}
                                                        className="form-control-enhanced modern-radio"
                                                        value={p.Id}
                                                        control={<Radio />}
                                                        label={
                                                            <Tooltip title={p.ToolTipText ?? ''}
                                                                PopperProps={{ modifiers: [{ name: 'offset', options: { offset: [50, 0] } }] }}>
                                                                <span>{p.Caption ? p.Caption : p.Id}</span>
                                                            </Tooltip>
                                                        }
                                                        onMouseEnter={() => setIsPackageFormControlHovered(true)}
                                                        onMouseLeave={() => setIsPackageFormControlHovered(false)}
                                                        onChange={() => {
                                                            handlePackageChange(p.Caption ? p.Caption : p.Id, p.ValidDeliveryTypes ?? []);
                                                            setPackageId(p.Id ?? "")
                                                            setAltPackageId(p.AlternatePackageId ?? "");
                                                            setParentPackageId(p.ParentPackageId ?? "");                                                           
                                                        }}
                                                    />
                                                </Fragment>
                                            )}
                                        </RadioGroup>
                                    </StyledPackageFormControl>
                                </StyledPackageCardContent>
                            </StyledPackageCard>
                        </StyledPackageTooltip>
                    </Grid>
                    {/* Delivery Method Card */}
                    <Grid item xs={12} md={6} lg={6}>
                        <StyledDeliveryCard className="responsive-card">
                            <StyledDeliveryCardHeader 
                                requestType={requestType}
                                title="Delivery Method">
                            </StyledDeliveryCardHeader>
                            <StyledDeliveryCardContent>
                                {requestType === REQUEST_TYPES.LIGHTS_OUT &&
                                    <StyledDeliveryFormGroup>
                                        {deliveryMethods.map((dm, i) =>
                                            <StyledDeliveryFormControlLabel
                                                className={`form-control-enhanced ${ (allowMultiDelivery ?? true) ? 'modern-checkbox' : 'modern-radio' }`}
                                                control={
                                                    <Tooltip
                                                        title={dm.ToolTipText ?? ''}
                                                        PopperProps={{ modifiers: [{ name: 'offset', options: { offset: [50, 0] } }] }}
                                                    >
                                                        {(allowMultiDelivery ?? true) ? (
                                                            <Checkbox
                                                                value={dm.Id}
                                                                checked={selectedDeliveryMethods.some(selected => selected.Id === dm.Id)}
                                                                onChange={(e) => handleDeliveryMethodClick(e)}
                                                            />
                                                        ) : (
                                                            <Radio
                                                                value={dm.Id}
                                                                checked={selectedDeliveryMethods.length > 0 && selectedDeliveryMethods[0].Id === dm.Id}
                                                                onChange={(e) => handleDeliveryMethodClick(e)}
                                                            />
                                                        )}
                                                    </Tooltip>
                                                }
                                                label={
                                                    <Tooltip
                                                        title={dm.ToolTipText ?? ''}
                                                        PopperProps={{ modifiers: [{ name: 'offset', options: { offset: [50, 0] } }] }}
                                                    >
                                                        <span>{dm.Caption ? dm.Caption : dm.Id}</span>
                                                    </Tooltip>
                                                }
                                                key={`dmethod-${i}`}
                                            />

                                        )}
                                    </StyledDeliveryFormGroup>
                                }
                            </StyledDeliveryCardContent>
                        </StyledDeliveryCard>
                    </Grid>
                    {/* Recent Orders */}
                    <Grid item xs={6}>
                        <AttachmentsCard visible={attachmentsVisible} enabled={attachmentsEnable} title={attachmentsTitle} />
                    </Grid>
                    <Grid item xs={6}>
                        {configMisc?.RequestTypeAvailability !== REQUEST_TYPES.LIGHTS_ON &&
                            <SelectToPrintCard
                                packageId={packageId}
                                altPackageId={altPackageId}
                                parentPackageId={parentPackageId}
                                visible={selectedVisible}
                                enabled={selectedEnable}
                                title={selectToPrintTitle}
                                docList={documentList}
                                selectedDocList={selectedDocumentList}
                                setListMethod={setDocumentList}
                                setSelectedListMethod={setSelectedDocumentList}
                            />
                        }
                    </Grid>
                </Grid>
            </StyledContentContainer>
            {(logsTabVisible || requestResponseTabVisible) && (
                <StyledTabsContainer maxWidth="xl">
                    <Grid item xs={12} lg={12} xl={12}>
                        <OrderTabs
                            logsTabVisible={logsTabVisible}
                            logsTabEnabled={logsTabEnable}
                            logsTabTitle={logsTabTitle}
                            requestResponseTabVisible={requestResponseTabVisible}
                            requestResponseTabEnabled={requestResponseTabEnable}
                            requestResponseTabTitle={requestResponseTabTitle}
                            requests={requests} authToken={appState.accessToken ?? ""}
                            requestType={requestType}
                        />
                    </Grid>
                </StyledTabsContainer>
            )}
            <StyledButtonContainer>
                <Grid item xs={12} justifyContent="end">
                    <Stack direction="row" justifyContent="end" spacing={1}>
                        <Button variant="contained" size="small" color="info" onClick={() => setAutoEvaluationResultsOpen(true)} >Why can't I disclose?</Button>
                    </Stack>
                </Grid>
            </StyledButtonContainer>
        </StyledMainContainer>
        <DtDialog 
            maxWidth="lg"
            open={autoEvaluationResultsOpen}
            onClose={() => setAutoEvaluationResultsOpen(false)}
            title="Control Evaluation Results"
            sx={{content: {pt: 0, pb: 1}}}>
            <EvaluationResults
                packageList={allPackageTypes}
                deliveryTypeList={deliveryMethodState}
                otherControlList={otherControlsState}
            />
        </DtDialog>
        </div>
    )
};

export default OrderPage;
